import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
  Switch,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { homeownersInsuranceInfoService } from '../../../../services/homeownersInsuranceInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('HomeownersInsuranceInput');

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const HomeownersInsuranceInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { homeownersInsuranceId?: string; isEdit?: boolean }>, string>>();
  const [showStateModal, setShowStateModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPolicyNumber, setShowPolicyNumber] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const homeownersInsuranceId = (route.params as any)?.homeownersInsuranceId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Format currency with commas
  const formatCurrency = (value: string) => {
    // Remove non-numeric characters except decimal
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Split by decimal
    const parts = numericValue.split('.');
    // Format whole number with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Limit decimal places to 2
    if (parts[1]) {
      parts[1] = parts[1].substring(0, 2);
    }
    return parts.join('.');
  };

  // Format date as DD/MM/YYYY
  const formatDate = (value: string) => {
    // Remove non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    let formatted = '';
    
    if (numericValue.length <= 2) {
      formatted = numericValue;
    } else if (numericValue.length <= 4) {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2);
    } else {
      formatted = numericValue.slice(0, 2) + '/' + numericValue.slice(2, 4) + '/' + numericValue.slice(4, 8);
    }
    
    return formatted;
  };

  // Helper: parse concatenated address back into components
  const parseAddress = (propertyAddress: string) => {
    if (!propertyAddress) {
      return { street: '', city: '', state: '', zip: '' };
    }

    let street = '';
    let city = '';
    let state = '';
    let zip = '';
    
    // Try to extract zip (5 digits at the end)
    const zipMatch = propertyAddress.match(/\b(\d{5})\s*$/);
    if (zipMatch) {
      zip = zipMatch[1];
      const remaining = propertyAddress.replace(/\b\d{5}\s*$/, '').trim();
      
      // Try to extract state (2 letters before zip, potentially after comma)
      const stateMatch = remaining.match(/,?\s*([A-Z]{2})\s*$/);
      if (stateMatch) {
        state = stateMatch[1];
        const streetAndCity = remaining.replace(/,?\s*[A-Z]{2}\s*$/, '').trim();
        
        // Find the last comma to separate city from street
        const lastCommaIndex = streetAndCity.lastIndexOf(',');
        if (lastCommaIndex > -1) {
          street = streetAndCity.substring(0, lastCommaIndex).trim();
          city = streetAndCity.substring(lastCommaIndex + 1).trim();
        } else {
          // No comma found, try to split by looking for common street patterns
          const parts = streetAndCity.split(' ');
          if (parts.length > 1) {
            if (parts.length <= 3) {
              street = parts.slice(0, -1).join(' ');
              city = parts[parts.length - 1];
            } else {
              street = parts.slice(0, -2).join(' ');
              city = parts.slice(-2).join(' ');
            }
          } else {
            street = streetAndCity;
          }
        }
      } else {
        street = remaining;
      }
    } else {
      // No zip found, try to parse what we can
      const commaIndex = propertyAddress.lastIndexOf(',');
      if (commaIndex > -1) {
        street = propertyAddress.substring(0, commaIndex).trim();
        const afterComma = propertyAddress.substring(commaIndex + 1).trim();
        
        const stateMatch = afterComma.match(/^([A-Z]{2})$/);
        if (stateMatch) {
          state = stateMatch[1];
        } else {
          city = afterComma;
        }
      } else {
        street = propertyAddress;
      }
    }
    
    return { street, city, state, zip };
  };

  const [formData, setFormData] = useState({
    insuranceCompany: '',
    policyNumber: '',
    agentName: '',
    agentPhone: '',
    agentEmail: '',
    policyStartDate: '',
    policyEndDate: '',
    coverageAmount: '',
    deductible: '',
    annualPremium: '',
    paymentFrequency: '',
    autopayEnabled: false,
    street: '',
    city: '',
    state: '',
    zip: '',
    claimPhone: '',
    website: '',
    username: '',
    password: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [savedHomeownersInsuranceId, setSavedHomeownersInsuranceId] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit && homeownersInsuranceId) {
      (async () => {
        try {
          // Load existing homeowners insurance record
          const record = await homeownersInsuranceInfoService.getById(homeownersInsuranceId);
          if (record) {
            // Parse the address back into components
            const addressParts = parseAddress(record.propertyAddress || '');
            
            setFormData(prev => ({
              ...prev,
              insuranceCompany: record.insuranceCompany || '',
              policyNumber: record.policyNumber || '',
              agentName: record.agentName || '',
              agentPhone: formatPhoneNumber(record.agentPhone || ''),
              agentEmail: record.agentEmail || '',
              policyStartDate: formatDate(record.policyStartDate || ''),
              policyEndDate: formatDate(record.policyEndDate || ''),
              coverageAmount: record.coverageAmount ? formatCurrency(record.coverageAmount.toString()) : '',
              deductible: record.deductible ? formatCurrency(record.deductible.toString()) : '',
              annualPremium: record.annualPremium ? formatCurrency(record.annualPremium.toString()) : '',
              paymentFrequency: record.paymentFrequency || '',
              autopayEnabled: record.autopayEnabled || false,
              street: addressParts.street,
              city: addressParts.city,
              state: addressParts.state,
              zip: addressParts.zip,
              claimPhone: formatPhoneNumber(record.claimPhone || ''),
              notes: record.notes || '',
            }));
            // Try to load portal credentials
            try {
              const creds = await vaultService.getPortalCredentials(homeownersInsuranceId, 'homeowners-insurance');
              if (creds && (creds.username || creds.password || creds.accountNumber)) {
                setHasOnlinePortal(true);
                setFormData(prev => ({
                  ...prev,
                  policyNumber: creds.accountNumber || record.policyNumber || '',
                  username: creds.username || '',
                  password: creds.password || '',
                }));
              }
            } catch {}
          }
        } catch (e) {
          logger.debug('Failed to load homeowners insurance for edit', e);
        }
      })();
    }
  }, [isEdit, homeownersInsuranceId]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password || formData.policyNumber) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      // Build backend payload
      const propertyAddress = [
        formData.street?.trim(),
        [formData.city?.trim(), formData.state?.trim()].filter(Boolean).join(', '),
        formData.zip?.trim(),
      ]
        .filter(Boolean)
        .join(' ');

      const payload: any = {
        insuranceCompany: formData.insuranceCompany || undefined,
        policyNumber: formData.policyNumber || undefined,
        agentName: formData.agentName || undefined,
        agentPhone: formData.agentPhone.replace(/\D/g, ''),
        agentEmail: formData.agentEmail.toLowerCase() || undefined,
        policyStartDate: formData.policyStartDate || undefined,
        policyEndDate: formData.policyEndDate || undefined,
        coverageAmount: formData.coverageAmount ? parseFloat(formData.coverageAmount.replace(/[^0-9.]/g, '')) : undefined,
        deductible: formData.deductible ? parseFloat(formData.deductible.replace(/[^0-9.]/g, '')) : undefined,
        annualPremium: formData.annualPremium ? parseFloat(formData.annualPremium.replace(/[^0-9.]/g, '')) : undefined,
        paymentFrequency: formData.paymentFrequency || undefined,
        autopayEnabled: formData.autopayEnabled,
        propertyAddress: propertyAddress || undefined,
        claimPhone: formData.claimPhone.replace(/\D/g, ''),
        notes: formData.notes || undefined,
      };

      // Create or update homeowners insurance record
      let savedId = homeownersInsuranceId as string | undefined;
      if (isEdit && homeownersInsuranceId) {
        const updated = await homeownersInsuranceInfoService.update(homeownersInsuranceId, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
      } else {
        const saved = await homeownersInsuranceInfoService.create(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store the saved ID for the Done button
      setSavedHomeownersInsuranceId(savedId);

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password || formData.policyNumber)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for homeowners insurance ID:', savedId);
          await vaultService.storePortalCredentials(savedId, 'homeowners-insurance', {
            accountNumber: formData.policyNumber,
            username: formData.username,
            password: formData.password,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for homeowners insurance portal credentials:', e);
        }
      }

      // Show Add Another / Done buttons after successful save
      setShowAddAnotherDone(true);
    } catch (error: any) {
      logger.error('Error saving homeowners insurance info:', error);
      Alert.alert('Error', 'Failed to save homeowners insurance information. Please try again.');
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Clear form and hide buttons
    setFormData({
      insuranceCompany: '',
      policyNumber: '',
      agentName: '',
      agentPhone: '',
      agentEmail: '',
      policyStartDate: '',
      policyEndDate: '',
      coverageAmount: '',
      deductible: '',
      annualPremium: '',
      paymentFrequency: '',
      autopayEnabled: false,
      street: '',
      city: '',
      state: '',
      zip: '',
      claimPhone: '',
      website: '',
      username: '',
      password: '',
      notes: '',
    });
    setHasOnlinePortal(false);
    setShowAddAnotherDone(false);
    setSubmitting(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedHomeownersInsuranceId || homeownersInsuranceId;
    logger.debug('[Done Debug] Using ID:', idToUse);
    (navigation as any).navigate('HomeownersInsuranceLockUnlock', { homeownersInsuranceId: idToUse });
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state });
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          {isEdit ? 'Edit' : 'Add'} Homeowners Insurance Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Insurance Company Name</Text>
            <TextInput
              style={styles.input}
              value={formData.insuranceCompany}
              onChangeText={(text) => setFormData({ ...formData, insuranceCompany: text })}
              placeholder="Enter Insurance Company Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Agent Name</Text>
            <TextInput
              style={styles.input}
              value={formData.agentName}
              onChangeText={(text) => setFormData({ ...formData, agentName: text })}
              placeholder="Enter Agent Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Agent Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.agentPhone}
              onChangeText={(text) => setFormData({ ...formData, agentPhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Agent Email</Text>
            <TextInput
              style={styles.input}
              value={formData.agentEmail}
              onChangeText={(text) => setFormData({ ...formData, agentEmail: text.toLowerCase() })}
              placeholder="Enter Agent Email"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Claims Phone Number</Text>
            <TextInput
              style={styles.input}
              value={formData.claimPhone}
              onChangeText={(text) => setFormData({ ...formData, claimPhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Property Address</Text>
            <TextInput
              style={styles.input}
              value={formData.street}
              onChangeText={(text) => setFormData({ ...formData, street: text })}
              placeholder="Street Address"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="City"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.rowContainer}>
            <View style={[styles.inputGroup, { flex: 1, marginRight: 10 }]}>
              <TouchableOpacity
                style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
                onPress={() => setShowStateModal(true)}
              >
                <Text style={[
                  { fontSize: 16, color: '#1C2C4F' },
                  !formData.state && { color: '#A9A9A9' }
                ]}>
                  {formData.state || 'Select State'}
                </Text>
                <Image
                  source={require('../../../../assets/icons/down.png')}
                  style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
                />
              </TouchableOpacity>
            </View>
            <View style={[styles.inputGroup, { flex: 1 }]}>
              <TextInput
                style={styles.input}
                value={formData.zip}
                onChangeText={(text) => setFormData({ ...formData, zip: text })}
                placeholder="Zip"
                placeholderTextColor="#A9A9A9"
                keyboardType="number-pad"
                maxLength={5}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Policy Start Date</Text>
            <TextInput
              style={styles.input}
              value={formData.policyStartDate}
              onChangeText={(text) => setFormData({ ...formData, policyStartDate: formatDate(text) })}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Policy End Date</Text>
            <TextInput
              style={styles.input}
              value={formData.policyEndDate}
              onChangeText={(text) => setFormData({ ...formData, policyEndDate: formatDate(text) })}
              placeholder="DD/MM/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Coverage Amount</Text>
            <TextInput
              style={styles.input}
              value={formData.coverageAmount ? `$${formData.coverageAmount}` : ''}
              onChangeText={(text) => {
                const cleaned = text.replace(/^\\$/, '');
                setFormData({ ...formData, coverageAmount: formatCurrency(cleaned) });
              }}
              placeholder="$0,000,000"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Deductible</Text>
            <TextInput
              style={styles.input}
              value={formData.deductible ? `$${formData.deductible}` : ''}
              onChangeText={(text) => {
                const cleaned = text.replace(/^\\$/, '');
                setFormData({ ...formData, deductible: formatCurrency(cleaned) });
              }}
              placeholder="$0,000"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Annual Premium</Text>
            <TextInput
              style={styles.input}
              value={formData.annualPremium ? `$${formData.annualPremium}` : ''}
              onChangeText={(text) => {
                const cleaned = text.replace(/^\\$/, '');
                setFormData({ ...formData, annualPremium: formatCurrency(cleaned) });
              }}
              placeholder="$0,000"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Payment Frequency</Text>
            <TextInput
              style={styles.input}
              value={formData.paymentFrequency}
              onChangeText={(text) => setFormData({ ...formData, paymentFrequency: text })}
              placeholder="Monthly, Quarterly, Annually"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Autopay Enabled</Text>
              <TouchableOpacity
                style={styles.toggleButton}
                onPress={() => setFormData({ ...formData, autopayEnabled: !formData.autopayEnabled })}
              >
                <View style={[styles.toggle, formData.autopayEnabled && styles.toggleActive]}>
                  <View style={[styles.toggleThumb, formData.autopayEnabled && styles.toggleThumbActive]} />
                </View>
              </TouchableOpacity>
            </View>
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
            >
              <View style={[styles.toggle, hasOnlinePortal && styles.toggleActive]}>
                <View style={[styles.toggleThumb, hasOnlinePortal && styles.toggleThumbActive]} />
              </View>
              <Text style={styles.toggleLabel}>Does your insurance have an online portal?</Text>
            </TouchableOpacity>
            <Text style={styles.helperText}>
              If yes, enter your login credentials. These will be securely stored in your vault and will only be shared with your successor after your End of Life event.
            </Text>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Policy Number</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.policyNumber}
                    onChangeText={(text) => setFormData({ ...formData, policyNumber: text })}
                    placeholder="Enter Policy Number"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPolicyNumber}
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPolicyNumber(!showPolicyNumber)}
                  >
                    <Image
                      source={showPolicyNumber ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Website</Text>
                <TextInput
                  style={styles.input}
                  value={formData.website}
                  onChangeText={(text) => setFormData({ ...formData, website: text })}
                  placeholder="Enter Website URL"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="url"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password</Text>
                <View style={styles.passwordContainer}>
                  <TextInput
                    style={[styles.input, styles.passwordInput]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                  />
                  <TouchableOpacity
                    style={styles.eyeButton}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIcon}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes or policy location details"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
            />
          </View>

          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={[styles.submitButton, styles.addAnotherButton]} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.submitButton, styles.doneButton]} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.submitButtonContainer}>
              {submitting ? (
                <ActivityIndicator size="large" color="#29BFC0" />
              ) : (
                <TouchableOpacity 
                  style={styles.submitButton} 
                  onPress={handleSubmit}
                >
                  <Text style={styles.submitButtonText}>
                    {isEdit ? 'Update' : 'Submit'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent={true}
        animationType="slide"
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity onPress={() => setShowStateModal(false)}>
                <Text style={styles.modalCloseText}>×</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(10),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(10),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: ms(10),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    padding: ms(20),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  placeholderText: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#A9A9A9',
  },
  rowContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(10),
  },
  toggleButton: {
    marginLeft: ms(15),
  },
  toggle: {
    width: s(50),
    height: s(30),
    borderRadius: ms(15),
    backgroundColor: '#E5E7EB',
    marginRight: ms(15),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleActive: {
    backgroundColor: '#29BFC0',
  },
  toggleThumb: {
    width: s(26),
    height: s(26),
    borderRadius: ms(13),
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleThumbActive: {
    alignSelf: 'flex-end',
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    lineHeight: ms(18, 0.3),
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: ms(50),
  },
  eyeButton: {
    position: 'absolute',
    right: ms(15),
    top: ms(15),
    padding: ms(5),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
    gap: ms(20),
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F5A',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    alignItems: 'center',
    minWidth: s(140),
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseText: {
    fontSize: ms(28, 0.3),
    color: '#1C2C4F',
    paddingHorizontal: ms(8),
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#e2e8f0',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});